import { useState } from "react";
import { CheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { CARD, HINT } from "@/onboarding/ui/theme";
import { visibleSteps } from "../visibleSteps";
import { trackDemoGuide } from "../analytics";
import { useDemoGuideStore } from "../useDemoGuideStore";

/**
 * What's left to try, once the rail has been closed.
 *
 * Bottom-LEFT, because Tawk owns bottom-right. Hidden on small screens for the
 * same reason the onboarding checklist is: the mobile carousel and the chat
 * bubble already crowd that corner.
 *
 * Fully ignorable and dismissible. It exists so that closing the guide isn't
 * a one-way door — someone who wanted to look around first can pick the run
 * back up, and the popout entry stays reachable whether or not they ever
 * started it.
 */
export default function DemoChecklistLauncher() {
  const [open, setOpen] = useState(false);
  const { state, resumeRun, restart, dismissChecklist } = useDemoGuideStore(
    (s) => ({
      state: s.state,
      resumeRun: s.resumeRun,
      restart: s.restart,
      dismissChecklist: s.dismissChecklist,
    })
  );

  const assetClass = state?.assetClass;
  const steps = assetClass ? visibleSteps(assetClass) : [];
  const runDone = !!state?.completedAt;

  /**
   * The run is the unit, and it is the only one.
   *
   * Popping a widget out was listed separately, which read as a contradiction
   * the moment anyone SKIPPED that step: `poppedOutAt` is only written when a
   * widget is genuinely popped out, so the panel showed "Finish the
   * walkthrough (4/4)" ticked above an outstanding item from inside it.
   * Skipping is a legitimate way through a guided run; a checklist that calls
   * the run complete and one of its steps outstanding is just wrong on its
   * face.
   *
   * Deliberately no early return when it is done. This panel is also the only
   * route back — "start the demo again" lives in it — so hiding it on
   * completion removed the way back in at the exact moment someone might want
   * it. It stays until dismissed.
   */
  const doneCount = runDone ? 1 : 0;
  const total = 1;

  const toggle = () => {
    if (!open) trackDemoGuide("checklist_opened");
    setOpen((v) => !v);
  };

  return (
    // z-40 for the same reason as the rail: below every dialog, so it cannot
    // sit on top of an editor's controls.
    <div className="no-drag fixed bottom-4 left-4 z-40 hidden sm:block">
      {open && (
        <div className="mb-3 w-96 rounded-lg border border-gray-700 bg-gray-900 p-5 shadow-2xl">
          <div className="mb-3 flex items-start justify-between">
            <div>
              <p className="text-base font-semibold text-gray-100">
                Explore Newsquawk
              </p>
              <p className={HINT}>Pick it up whenever. Or don't.</p>
            </div>
            <button
              type="button"
              onClick={dismissChecklist}
              aria-label="Dismiss"
              data-analytics-id="demo-guide-checklist-dismiss"
              className="rounded p-0.5 text-gray-500 transition-colors hover:text-gray-300"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          <ul className="space-y-1.5">
            <li>
              <button
                type="button"
                disabled={runDone || !assetClass}
                onClick={() => {
                  resumeRun();
                  setOpen(false);
                }}
                data-analytics-id="demo-guide-checklist-resume"
                className={clsx(
                  CARD,
                  "flex w-full items-center gap-3 p-3.5 text-left text-sm transition-colors",
                  runDone || !assetClass
                    ? "cursor-default opacity-60"
                    : "cursor-pointer hover:border-squawk-blue/60"
                )}
              >
                <Tick done={runDone} />
                <span className={runDone ? "text-gray-400 line-through" : "text-gray-100"}>
                  {assetClass
                    ? `Finish the walkthrough (${Math.min(state!.step, steps.length)}/${steps.length})`
                    : "Pick an asset class to start"}
                </span>
              </button>
            </li>
          </ul>

          <button
            type="button"
            onClick={() => {
              restart();
              setOpen(false);
            }}
            data-analytics-id="demo-guide-checklist-restart"
            className="mt-3 w-full cursor-pointer border-none bg-transparent p-0 text-left text-xs text-gray-500 transition-colors hover:text-gray-300"
          >
            Start the demo again — pick a different asset class
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        data-analytics-id="demo-guide-checklist-toggle"
        className="flex cursor-pointer items-center gap-2.5 rounded-full border border-gray-700 bg-gray-800 py-3 pl-4 pr-5 text-base font-medium text-gray-200 shadow-2xl transition-colors hover:border-squawk-blue/60 hover:text-white"
      >
        <span className="text-sm font-semibold text-squawk-blue">
          {doneCount}/{total}
        </span>
        Explore Newsquawk
      </button>
    </div>
  );
}

function Tick({ done }: { done: boolean }) {
  return (
    <span
      className={clsx(
        "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
        done ? "border-squawk-blue bg-squawk-blue text-white" : "border-gray-600"
      )}
    >
      {done && <CheckIcon className="h-2.5 w-2.5" strokeWidth={3} />}
    </span>
  );
}
